import {
    Close,
    DescriptionOutlined,
    FullscreenExitOutlined,
    FullscreenOutlined,
    DownloadOutlined,
    ErrorOutlined,
    ImageOutlined,
    InsertDriveFileOutlined,
    PictureAsPdfOutlined,
} from '@mui/icons-material';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    type SxProps,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { renderAsync } from 'docx-preview';
import { heicTo } from 'heic-to';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import PDFViewer from 'components/common/PDFViewer/PDFViewer';
import Result from 'components/v2/common/Result';

import { axiosDefault } from 'api';
import useTranslate from 'hooks/useTranslate';
import { downloadBlob } from 'utils/file';

interface Props {
    open: boolean;
    mobile: boolean;
    onClose: () => void;
    fileUrl?: string | null;
    /** Already-fetched file. Skips the URL request when the caller has the bytes. */
    file?: Blob | null;
    fileName?: string;
    title?: string;
}

type FileKind = 'pdf' | 'image' | 'docx' | null;

const previewScrollSx: SxProps<Theme> = {
    height: '100%',
    width: '100%',
    boxSizing: 'border-box',
    overflow: 'auto',
};

const isHeicFile = (contentType: string, fileUrl: string, fileName: string): boolean => {
    const lowerContentType = contentType.toLowerCase();
    if (lowerContentType.includes('image/heic') || lowerContentType.includes('image/heif')) {
        return true;
    }

    // Strip query string / fragment, then decode to handle %2E or encoded extensions
    const urlPath = decodeURIComponent(fileUrl.split(/[?#]/)[0]).toLowerCase();
    if (urlPath.endsWith('.heic') || urlPath.endsWith('.heif')) {
        return true;
    }

    const lowerName = fileName.toLowerCase();
    if (lowerName.endsWith('.heic') || lowerName.endsWith('.heif')) {
        return true;
    }

    return false;
};

const detectFileType = (contentType: string, fileUrl: string, fileName: string): FileKind => {
    const lowerContentType = contentType.toLowerCase();
    const lowerUrl = fileUrl.toLowerCase();
    const lowerName = fileName.toLowerCase();

    if (
        lowerContentType.includes(
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        ) ||
        lowerContentType.includes('application/msword') ||
        /\.docx?([?#].*)?$/.test(lowerUrl) ||
        /\.docx?$/.test(lowerName)
    ) {
        return 'docx';
    }

    if (
        lowerContentType.startsWith('image/') ||
        /\.(png|jpe?g|gif|webp|bmp|svg|heic|heif)([?#].*)?$/.test(lowerUrl) ||
        /\.(png|jpe?g|gif|webp|bmp|svg|heic|heif)$/.test(lowerName)
    ) {
        return 'image';
    }

    return 'pdf';
};

const KNOWN_FILE_EXTENSION =
    /\.(pdf|docx?|png|jpe?g|gif|webp|bmp|svg|heic|heif|xlsx?|pptx?|txt|csv)$/i;

const resolveDownloadFileName = (
    fileName: string,
    fileUrl: string,
    contentType: string,
    fileType: FileKind
): string => {
    const baseName = (fileName || 'document').trim() || 'document';
    if (KNOWN_FILE_EXTENSION.test(baseName)) {
        return baseName;
    }

    const urlPath = decodeURIComponent((fileUrl || '').split(/[?#]/)[0]);
    const urlExt = urlPath.match(KNOWN_FILE_EXTENSION)?.[0]?.toLowerCase();
    if (urlExt) {
        return `${baseName}${urlExt}`;
    }

    const lowerType = contentType.toLowerCase();
    if (lowerType.includes('pdf')) return `${baseName}.pdf`;
    if (lowerType.includes('wordprocessingml') || lowerType.includes('msword')) {
        return `${baseName}.docx`;
    }
    if (lowerType.includes('png')) return `${baseName}.png`;
    if (lowerType.includes('gif')) return `${baseName}.gif`;
    if (lowerType.includes('webp')) return `${baseName}.webp`;
    if (lowerType.includes('svg')) return `${baseName}.svg`;
    if (lowerType.includes('heic')) return `${baseName}.heic`;
    if (lowerType.includes('heif')) return `${baseName}.heif`;
    if (lowerType.includes('jpeg') || lowerType.includes('jpg')) return `${baseName}.jpg`;

    if (fileType === 'docx') return `${baseName}.docx`;
    if (fileType === 'image') return `${baseName}.jpg`;
    return `${baseName}.pdf`;
};

const DocumentPreview = ({ open, mobile, onClose, fileUrl, file, fileName = '', title }: Props) => {
    const { translate } = useTranslate();
    const theme = useTheme();
    const isCompact = useMediaQuery(theme.breakpoints.down('md')) || mobile;
    const [expanded, setExpanded] = useState(false);
    const fullScreen = isCompact || expanded;
    const docxContainerRef = useRef<HTMLDivElement | null>(null);
    const [pathToPDF, setPathToPDF] = useState<string | null>(null);
    const [docxBuffer, setDocxBuffer] = useState<ArrayBuffer | null>(null);
    const [fileType, setFileType] = useState<FileKind>(null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    // Original file bytes — used so Download saves with `fileName`, not the S3 key
    const [sourceBlob, setSourceBlob] = useState<Blob | null>(null);
    const [downloadContentType, setDownloadContentType] = useState('');
    const [isDownloading, setIsDownloading] = useState(false);

    useEffect(() => {
        if (!open || (!fileUrl && !file)) {
            setPathToPDF(null);
            setDocxBuffer(null);
            setFileType(null);
            setLoadError(null);
            setLoading(false);
            setSourceBlob(null);
            setDownloadContentType('');
            setIsDownloading(false);
            setExpanded(false);
            return;
        }

        let cancelled = false;

        const loadPreview = async () => {
            setLoading(true);
            setLoadError(null);
            setPathToPDF(null);
            setDocxBuffer(null);
            setFileType(null);
            setSourceBlob(null);
            setDownloadContentType('');

            try {
                let contentType = '';
                let fileData: ArrayBuffer | null = null;

                if (file) {
                    fileData = await file.arrayBuffer();
                    contentType = file.type || '';
                    if (cancelled) {
                        return;
                    }
                    setSourceBlob(file);
                    setDownloadContentType(contentType);
                } else if (fileUrl) {
                    const response = await axiosDefault.get<ArrayBuffer>(fileUrl, {
                        responseType: 'arraybuffer',
                        headers: { 'Content-Type': 'binary/octet-stream' },
                    });

                    if (cancelled) {
                        return;
                    }

                    contentType = String(response.headers?.['content-type'] || '');
                    fileData = response.data;
                    // Keep original bytes for named download (not HEIC→JPEG preview conversion)
                    setSourceBlob(
                        new Blob([response.data], contentType ? { type: contentType } : undefined)
                    );
                    setDownloadContentType(contentType);
                }

                if (!fileData) return;

                let previewData: ArrayBuffer = fileData;
                let effectiveContentType = contentType;
                if (isHeicFile(contentType, fileUrl || '', fileName)) {
                    try {
                        const heicBlob = new Blob([fileData]);
                        const jpegBlob = await heicTo({
                            blob: heicBlob,
                            type: 'image/jpeg',
                            quality: 0.85,
                        });
                        previewData = await jpegBlob.arrayBuffer();
                        effectiveContentType = 'image/jpeg';
                    } catch {
                        if (!cancelled) {
                            setLoadError(
                                'This HEIC image could not be converted for preview. Try downloading the file instead.'
                            );
                            setLoading(false);
                        }
                        return;
                    }
                }

                if (cancelled) {
                    return;
                }

                const detectedType = detectFileType(effectiveContentType, fileUrl || '', fileName);
                setFileType(detectedType);

                if (detectedType === 'docx') {
                    setDocxBuffer(previewData);
                    return;
                }

                const blobType =
                    effectiveContentType ||
                    (detectedType === 'pdf' ? 'application/pdf' : undefined);
                const fileBlob = new Blob([previewData], blobType ? { type: blobType } : undefined);

                const fileBase64 = await new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.readAsDataURL(fileBlob);
                    reader.onload = () => resolve(reader.result as string);
                    reader.onerror = (error) => reject(error);
                });

                if (!cancelled) {
                    setPathToPDF(fileBase64);
                }
            } catch {
                if (!cancelled) {
                    setLoadError('Unable to load document. The link may be expired or missing.');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        void loadPreview();

        return () => {
            cancelled = true;
        };
    }, [file, fileName, fileUrl, open]);

    useEffect(() => {
        if (fileType !== 'docx' || !docxBuffer || !docxContainerRef.current) {
            return;
        }

        docxContainerRef.current.innerHTML = '';
        void renderAsync(docxBuffer, docxContainerRef.current);
    }, [docxBuffer, fileType]);

    const handleDownload = useCallback(async () => {
        if (!sourceBlob && !fileUrl) {
            return;
        }

        const downloadName = resolveDownloadFileName(
            fileName,
            fileUrl || '',
            downloadContentType,
            fileType
        );

        try {
            setIsDownloading(true);

            if (sourceBlob) {
                downloadBlob(sourceBlob, downloadName);
                return;
            }

            // Preview blob missing — fetch once more and save with fileName (never open S3 tab)
            const response = await axiosDefault.get<ArrayBuffer>(fileUrl!, {
                responseType: 'arraybuffer',
                headers: { 'Content-Type': 'binary/octet-stream' },
            });
            const contentType = String(response.headers?.['content-type'] || '');
            const blob = new Blob([response.data], contentType ? { type: contentType } : undefined);
            downloadBlob(
                blob,
                resolveDownloadFileName(fileName, fileUrl || '', contentType, fileType)
            );
        } catch {
            // Intentionally no window.open(fileUrl) — avoid S3 key as the download name
        } finally {
            setIsDownloading(false);
        }
    }, [sourceBlob, downloadContentType, fileName, fileType, fileUrl]);

    const typeChip = useMemo(() => {
        switch (fileType) {
            case 'pdf':
                return {
                    label: 'PDF',
                    icon: <PictureAsPdfOutlined />,
                    color: 'error' as const,
                };
            case 'image':
                return {
                    label: 'Image',
                    icon: <ImageOutlined />,
                    color: 'info' as const,
                };
            case 'docx':
                return {
                    label: 'DOCX',
                    icon: <DescriptionOutlined />,
                    color: 'primary' as const,
                };
            default:
                return {
                    label: 'File',
                    icon: <InsertDriveFileOutlined />,
                    color: 'default' as const,
                };
        }
    }, [fileType]);

    const heading = title || 'Document preview';
    const canDownload = Boolean(sourceBlob || fileUrl);

    let previewContent: React.ReactNode = null;

    if (pathToPDF && fileType === 'pdf') {
        previewContent = <PDFViewer file={pathToPDF} />;
    } else if (pathToPDF && fileType === 'image') {
        previewContent = (
            <Box
                component="img"
                src={pathToPDF}
                alt={fileName || 'Document preview'}
                sx={{
                    display: 'block',
                    margin: 'auto',
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    borderRadius: 1,
                    boxShadow: 1,
                }}
            />
        );
    } else if (fileType === 'docx' && docxBuffer) {
        previewContent = <Box ref={docxContainerRef} sx={{ width: '100%', minHeight: '100%' }} />;
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="xl"
            fullScreen={fullScreen}
            aria-labelledby="document-preview-title"
            slotProps={{
                paper: {
                    sx: {
                        height: fullScreen ? '100%' : '88vh',
                        maxHeight: fullScreen ? '100%' : '88vh',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        borderRadius: fullScreen ? 0 : 2,
                    },
                },
            }}
        >
            <DialogTitle
                component="div"
                id="document-preview-title"
                sx={{
                    px: { xs: 1.5, sm: 2.5 },
                    py: 1.5,
                    borderBottom: 1,
                    borderColor: 'divider',
                    flexShrink: 0,
                    minWidth: 0,
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        minWidth: 0,
                        width: '100%',
                    }}
                >
                    <Box sx={{ minWidth: 0, flex: '1 1 auto', overflow: 'hidden' }}>
                        <Typography
                            variant="subtitle1"
                            component="h2"
                            noWrap
                            sx={{ fontWeight: 700, lineHeight: 1.3 }}
                        >
                            {heading}
                        </Typography>
                        {fileName ? (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                noWrap
                                title={fileName}
                                sx={{ display: 'block', mt: 0.25 }}
                            >
                                {fileName}
                            </Typography>
                        ) : null}
                    </Box>

                    {!loading && fileType ? (
                        <Chip
                            size="small"
                            icon={typeChip.icon}
                            label={typeChip.label}
                            color={typeChip.color}
                            variant="outlined"
                            sx={{
                                flexShrink: 0,
                                fontWeight: 600,
                                '& .MuiChip-icon': {
                                    fontSize: 16,
                                    ml: 0.5,
                                },
                            }}
                        />
                    ) : null}

                    <Stack
                        direction="row"
                        spacing={0.75}
                        sx={{ alignItems: 'center', flexShrink: 0 }}
                    >
                        {isCompact ? null : (
                            <Tooltip
                                title={
                                    expanded ? translate('exitFullScreen') : translate('fullScreen')
                                }
                            >
                                <IconButton
                                    size="small"
                                    onClick={() => setExpanded((current) => !current)}
                                    aria-label={
                                        expanded
                                            ? translate('exitFullScreen')
                                            : translate('fullScreen')
                                    }
                                >
                                    {expanded ? (
                                        <FullscreenExitOutlined fontSize="small" />
                                    ) : (
                                        <FullscreenOutlined fontSize="small" />
                                    )}
                                </IconButton>
                            </Tooltip>
                        )}
                        {fullScreen || mobile ? (
                            <Tooltip title={canDownload ? 'Download' : 'Download unavailable'}>
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={() => void handleDownload()}
                                        disabled={!canDownload || isDownloading}
                                        aria-label="Download document"
                                    >
                                        {isDownloading ? (
                                            <CircularProgress size={16} color="inherit" />
                                        ) : (
                                            <DownloadOutlined fontSize="small" />
                                        )}
                                    </IconButton>
                                </span>
                            </Tooltip>
                        ) : (
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => void handleDownload()}
                                startIcon={
                                    isDownloading ? (
                                        <CircularProgress size={14} color="inherit" />
                                    ) : (
                                        <DownloadOutlined />
                                    )
                                }
                                disabled={!canDownload || isDownloading}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                            >
                                {isDownloading ? 'Downloading…' : 'Download'}
                            </Button>
                        )}
                        <Tooltip title="Close">
                            <IconButton size="small" onClick={onClose} aria-label="Close preview">
                                <Close fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Box>
            </DialogTitle>

            <DialogContent
                sx={{
                    p: 0,
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    backgroundColor: 'grey.50',
                }}
            >
                {loading ? (
                    <Result
                        icon={<CircularProgress color="primary" size={28} />}
                        title="Loading preview"
                        description="Please wait while we prepare the document for viewing."
                        sx={{ background: 'transparent', borderColor: 'transparent' }}
                    />
                ) : null}

                {!loading && loadError ? (
                    <Result
                        icon={<ErrorOutlined sx={{ fontSize: 32 }} />}
                        title="Preview unavailable"
                        description={loadError}
                        sx={{ background: 'transparent', borderColor: 'transparent' }}
                    />
                ) : null}

                {!loading && !loadError && previewContent ? (
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            height: '100%',
                            ...(fileType === 'docx'
                                ? {
                                      overflow: 'auto',
                                      p: { xs: 1.5, md: 3 },
                                      backgroundColor: 'background.paper',
                                      '& .docx-wrapper': {
                                          background: 'transparent',
                                          padding: 0,
                                      },
                                      '& .docx': {
                                          margin: '0 auto',
                                          boxShadow: 1,
                                      },
                                  }
                                : {}),
                            ...(fileType === 'image'
                                ? {
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      p: { xs: 1.5, md: 3 },
                                      overflow: 'auto',
                                  }
                                : {}),
                            ...(fileType === 'pdf' ? { overflow: 'auto' } : {}),
                        }}
                    >
                        <Box
                            sx={{
                                ...previewScrollSx,
                                ...(fileType === 'image'
                                    ? {
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                      }
                                    : {}),
                            }}
                        >
                            {previewContent}
                        </Box>
                    </Box>
                ) : null}

                {!loading && !loadError && !previewContent ? (
                    <Result
                        icon={<InsertDriveFileOutlined sx={{ fontSize: 32 }} />}
                        title="No document available"
                        description="There is no document available to preview."
                        sx={{ background: 'transparent', borderColor: 'transparent' }}
                    />
                ) : null}
            </DialogContent>
        </Dialog>
    );
};

export default DocumentPreview;
